<template>
  <div class="stale-save-notice" :role="live ? 'alert' : null">

    <p v-if="lead" class="ssn-lead">{{lead}}</p>

    <!-- One line each (a summary: which approvals locked, which did not). -->
    <p v-for="(line, li) in lines" :key="'l' + li" class="ssn-line">{{line}}</p>

    <!-- Introduces the groups below ("These changes were not saved … :"). -->
    <p v-if="intro" class="ssn-intro">{{intro}}</p>

    <div v-for="(group, gi) in groups" :key="'g' + gi" class="ssn-group">
      <h5 class="ssn-row-label">{{group.rowLabel}}</h5>

      <div v-for="(field, fi) in group.fields" :key="'f' + gi + '-' + fi" class="ssn-field">

        <div class="ssn-label">
          <b>{{field.label}}</b>
          <el-button
            v-if="field.kind === 'text'"
            type="text"
            size="mini"
            :aria-label="copyAria(field.label)"
            @click="copyValue(field, gi + '-' + fi)">
            <i class="fa fa-copy" aria-hidden="true"></i>&nbsp;{{copiedKey === gi + '-' + fi ? copyDone : copyLabel}}
          </el-button>
        </div>

        <small v-if="field.changedByLine" class="text-muted ssn-changed-by">{{field.changedByLine}}</small>

        <div
          v-if="field.kind === 'text'"
          class="value-box"
          role="region"
          tabindex="0"
          :aria-label="'Your ' + field.label">{{field.yours}}</div>

        <div v-else-if="field.kind === 'scalar'" class="ssn-scalar">
          {{yoursLabel}} <b>{{field.yours}}</b>
          <template v-if="field.savedNow !== undefined"> &middot; {{savedNowLabel}} <b>{{field.savedNow}}</b></template>
        </div>

        <div v-else class="ssn-list">
          <div v-for="(line, li) in (field.added || [])" :key="'a' + li">{{addedLabel}} {{line}}</div>
          <div v-for="(line, ri) in (field.removed || [])" :key="'r' + ri">{{removedLabel}} {{line}}</div>
        </div>

      </div>
    </div>

    <p v-if="footer" class="ssn-footer">{{footer}}</p>
    <p v-if="savedLine" class="text-muted ssn-saved-line">{{savedLine}}</p>

    <div v-if="onRetry || onDismiss" class="ssn-actions">
      <el-button v-if="onRetry" type="primary" size="mini" @click="onRetry()">{{retryLabel}}</el-button>
      <el-button v-if="onDismiss" type="text" size="mini" @click="onDismiss()">{{dismissLabel}}</el-button>
    </div>

  </div>
</template>

<script>
/*
 * C-1 of the XOL-596 UX spec: the body of every "not saved" notice (stale_field,
 * the 403 locks, 422, network failures, request_closed), of the 428 dialog and of
 * the logout / "changes not saved yet" dialogs.
 *
 * Purely presentational: the caller (lib/syncNotices) passes strings that are
 * already labelled and formatted. It is rendered as a section of the C-12
 * SaveStatusPopup, or as the VNode `message` of an Element MessageBox (the hang,
 * logout and 428 dialogs), built with `$root.$createElement` so it outlives the
 * drawer that raised it.
 *
 * Props
 *   groups   [{ rowLabel, fields: [{ label, kind: 'text' | 'scalar' | 'list',
 *               yours, savedNow, added: [..], removed: [..], changedByLine }] }]
 *   lead, intro, footer, savedLine   Strings. `intro` sits between the lead and the
 *               groups and introduces them (it ends in a colon); `footer` follows them.
 *   lines    [String]: one paragraph each, after the lead (a summary's rows)
 *   onDismiss, onRetry        Function or null (their button shows only when set)
 *   live                      Boolean, default true: role="alert". The C-12 popup passes false.
 */
import { Button } from 'element-ui'
import { copyText } from '@/lib/requestSyncCopy'

export default {
  name: 'stale-save-notice',

  components: { ElButton: Button },

  props: {
    groups: {
      type: Array,
      default: function () { return [] }
    },
    lead: {
      type: String,
      default: null
    },
    intro: {
      type: String,
      default: null
    },
    lines: {
      type: Array,
      default: function () { return [] }
    },
    footer: {
      type: String,
      default: null
    },
    savedLine: {
      type: String,
      default: null
    },
    onDismiss: {
      type: Function,
      default: null
    },
    onRetry: {
      type: Function,
      default: null
    },
    // role="alert" for the dialogs. The C-12 popup passes false: it speaks through
    // its own live region, so the body must not be read twice.
    live: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      copiedKey: null,
      copyLabel: copyText('requestSync.copy.button'),
      copyDone: copyText('requestSync.copy.done'),
      yoursLabel: copyText('requestSync.notice.yours'),
      savedNowLabel: copyText('requestSync.notice.savedNow'),
      addedLabel: copyText('requestSync.notice.added'),
      removedLabel: copyText('requestSync.notice.removed'),
      retryLabel: copyText('requestSync.notice.retry'),
      dismissLabel: copyText('requestSync.notice.dismiss')
    }
  },

  methods: {

    copyAria (label) {
      return copyText('requestSync.copy.ariaYours', { label: label })
    },

    // navigator.clipboard where available, else a hidden textarea + execCommand
    // (production may be served over plain http).
    copyValue (field, key) {
      var self = this
      var text = field.yours === undefined || field.yours === null ? '' : String(field.yours)

      var done = function () {
        self.copiedKey = key
        self.$message({ type: 'success', message: copyText('requestSync.copy.done'), duration: 1500 })
        setTimeout(function () {
          if (self.copiedKey === key) {
            self.copiedKey = null
          }
        }, 2000)
      }

      var failed = function () {
        self.$message({ type: 'warning', message: copyText('requestSync.copy.failed') })
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, function () { self.fallbackCopy(text) ? done() : failed() })
        return
      }

      this.fallbackCopy(text) ? done() : failed()
    },

    fallbackCopy (text) {
      try {
        var ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        var ok = document.execCommand('copy')
        document.body.removeChild(ta)
        return !!ok
      } catch (e) {
        return false
      }
    }

  }
}
</script>

<style scoped>
.stale-save-notice { font-size: 13px; line-height: 1.4; }
.ssn-lead, .ssn-intro, .ssn-footer { margin: 0 0 8px; }
.ssn-line { margin: 0 0 4px; }
.ssn-row-label { margin: 8px 0 4px; font-size: 13px; }
.ssn-field { margin-bottom: 8px; }
.ssn-label { display: flex; align-items: center; justify-content: space-between; }
.value-box {
  white-space: pre-wrap;
  max-height: 8em;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 3px;
  padding: 4px 8px;
  background: #fafafa;
}
.ssn-actions { text-align: right; margin-top: 8px; }
</style>
